import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { Subject } from 'rxjs';
import { MegaMenuModule } from 'primeng/megamenu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { MegaMenuItem, MenuItem } from 'primeng/api';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  takeUntil,
} from 'rxjs/operators';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

import { SidebarService } from '../../core/services/sidebar';
import {
  UserProfile,
  UserSettingsService,
} from '@app/core/services/user-settings.service';
import { AuthService } from '@app/core/services/auth.service';
import { KeycloakService } from '@app/core/services/keycloak.service';
import { MenuConfigService } from '@app/core/services/menu-config.service';
import { PermissionService } from '@app/core/services/permission.service';
import { CommerceModulesService } from '@app/core/commerce/commerce-modules.service';
import { TenantSwitcherComponent } from '@app/layout/tenant-switcher/tenant-switcher.component';

interface UserInfo {
  name: string;
  email: string;
  avatar: string;
  role: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    MegaMenuModule,
    TieredMenuModule,
    CommonModule,
    PopoverModule,
    ButtonModule,
    AvatarModule,
    MenuModule,
    TranslateModule,
    TenantSwitcherComponent,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar implements OnInit, OnDestroy {
  protected readonly sidebarService = inject(SidebarService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly userSettingsService = inject(UserSettingsService);
  private readonly authService = inject(AuthService);
  private readonly keycloakService = inject(KeycloakService);
  private readonly menuConfigService = inject(MenuConfigService);
  private readonly permissionService = inject(PermissionService);
  private readonly commerceModules = inject(CommerceModulesService);

  readonly showTenantSwitcher = this.userSettingsService.showTenantSwitcher;

  isUserMenuOpen = signal(false);

  // Track current route for highlighting
  readonly currentRoute = signal<string>('');

  // Track current language for reactive role translation
  private readonly currentLang = signal<string>(
    this.translateService.currentLang || 'en',
  );

  // Computed signal for activated state to ensure reactivity
  readonly activatedState = computed(
    (): {
      parent: string | null;
      firstChild: string | null;
      secondChild: string | null;
    } => this.getActivatedState(),
  );

  // Computed signal for translated user role (reactive to language changes)
  // Gets raw role from authService and translates it
  readonly translatedRole = computed(() => {
    // const currentUser = this.authService.currentUser();
    const rawRole = 'User';
    // Access currentLang signal to make this reactive to language changes
    this.currentLang();
    return this.translateUserRole(rawRole);
  });

  // Template properties - explicitly declared
  isLogoHovered = false;
  userMenuItems: MenuItem[] = [];
  items: MegaMenuItem[] = [];

  // Hover panel properties
  hoveredItem: MegaMenuItem | null = null;
  hoverPanelPosition = { x: 0, y: 0 };

  // TieredMenu properties
  tieredMenuItems: MenuItem[] = [];
  tieredMenuVisible = false;
  tieredMenuPosition = { x: 0, y: 0 };
  isHoveringPanel = false;

  // Nested panel properties for 2-level structure
  firstLevelPanelVisible = false;
  firstLevelPanelPosition = { x: 0, y: 0 };
  firstLevelHoveredItem: MegaMenuItem | null = null;

  secondLevelPanelVisible = false;
  secondLevelPanelPosition = { x: 0, y: 0 };
  secondLevelHoveredItem: MenuItem | null = null;

  // Collapsed sidebar panel
  collapsedPanelVisible = false;
  collapsedPanelPosition = { x: 0, y: 0 };
  collapsedPanelHoveredItem: any = null;
  isHoveringCollapsedPanel = false;

  // Collapsed second level panel
  collapsedSecondLevelPanelVisible = false;
  collapsedSecondLevelPanelPosition = { x: 0, y: 0 };
  collapsedSecondLevelHoveredItem: any = null;
  isHoveringCollapsedSecondLevelPanel = false;

  // Enhanced state tracking for proper panel behavior
  isHoveringSidebar = false;
  isHoveringAnyPanel = false;
  panelHideTimeout: any = null;

  // Initialization flag to prevent hover interference during startup
  private isInitialized = false;

  // LocalStorage key for persisting sidebar state
  private readonly SIDEBAR_STATE_KEY = 'sidebar_status';

  isHoveringFirstLevelPanel = false;
  isHoveringSecondLevelPanel = false;

  // Timeouts for panel management
  firstLevelTimeout: any = null;
  secondLevelTimeout: any = null;
  menuItemTimeout: any = null;
  collapsedPanelTimeout: any = null;

  // Destroy subject for cleanup
  private readonly destroy$ = new Subject<void>();

  // Global mouse tracking for better hover detection
  private isMouseOverSidebar = false;
  private isMouseOverAnyPanel = false;
  private globalMouseLeaveTimeout: any = null;
  private panelHideFallbackTimeout: any = null;

  navigateToHomepage(): void {
    this.router.navigate(['/home']);
  }

  // User information from AuthService
  userInfo = signal<UserProfile>(this.initializeUserInfo());

  /**
   * Initialize user info from AuthService, localStorage, or default to Guest
   */
  private initializeUserInfo(): UserProfile {
    // First try AuthService
    const authUser = this.authService.currentUser();
    if (authUser) {
      return this.convertAuthUserToProfile(authUser);
    }

    // Then try localStorage
    try {
      const storedUserProfile = localStorage.getItem('userProfile');
      if (storedUserProfile) {
        const profile: UserProfile = JSON.parse(storedUserProfile);
        // Validate that we have essential fields
        if (profile && profile.username && profile.email) {
          return profile;
        }
      }
    } catch (error) {
      console.warn(
        '[Sidebar] Failed to parse userProfile from localStorage:',
        error,
      );
    }

    // Fallback to Guest User
    return this.convertAuthUserToProfile(null);
  }

  constructor() {
    // effect() must be called in constructor (injection context)
    effect(() => {
      const user = this.authService.currentUser();

      if (user) {
        this.userInfo.set(this.convertAuthUserToProfile(user));
      } else {
        // If no user from AuthService, try localStorage
        // Use setTimeout to avoid calling in effect context
        setTimeout(() => {
          this.loadUserInfoFromLocalStorage();
        }, 0);
      }
    });
  }

  ngOnInit(): void {
    // Initialize user settings service
    this.userSettingsService.initialize();

    // Load user info from AuthService (which may load from localStorage)
    this.loadUserInfo();

    // Also try to load from localStorage directly as fallback
    this.loadUserInfoFromLocalStorage();

    // Translations are already loaded by LanguageInitService during app initialization
    // Skip loading to avoid duplicate API calls - just initialize menu items
    // If translations are needed, they will be loaded by LanguageInitService
    this.initializeMenuItems();

    // Subscribe to permission changes to refresh menu when permissions are loaded
    this.permissionService.userPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Re-initialize menu items when permissions change
        this.initializeMenuItems();
        // Force change detection to ensure UI updates
        this.cdr.detectChanges();
      });

    // Also subscribe to permissionsLoaded$ to ensure menu refreshes when permissions are actually loaded
    this.permissionService.permissionsLoaded$
      .pipe(
        filter((loaded) => loaded), // Only when permissions are loaded
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        console.debug(
          '[Sidebar] Permissions loaded, re-initializing menu items',
        );
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });

    // Commerce menu items depend on /api/bo/modules, which may resolve after permissions
    this.commerceModules
      .loadModules()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });

    // Check if permissions are already loaded (handles race condition where permissions load before subscription)
    if (this.permissionService.arePermissionsLoaded()) {
      console.debug(
        '[Sidebar] Permissions already loaded, initializing menu items',
      );
      this.initializeMenuItems();
    }

    // Initialize menu items immediately (even if translations are still loading)
    // This ensures menu is visible immediately
    this.initializeMenuItems();

    // Listen for language changes from TranslateService directly
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLang.set(event.lang);
      this.initializeMenuItems();
      this.loadUserInfo(); // Reload user info (role translation is handled by computed signal)
    });

    // Refresh when the "show unavailable menu items" preference changes
    this.userSettingsService.userPreferences$
      .pipe(
        map((prefs) => prefs.showUnavailableMenuItems),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });

    // Add global mouse tracking for better hover detection
    this.setupGlobalMouseTracking();

    // Set up route tracking immediately
    this.setupRouteTracking();

    // Restore sidebar state from localStorage
    this.restoreSidebarStateFromStorage();

    // Ensure sidebar_status is saved initially
    setTimeout(() => {
      if (this.items.length > 0) {
        this.saveSidebarState();
      }
    }, 100);
  }

  /**
   * Restore complete sidebar state from localStorage
   */
  private restoreSidebarStateFromStorage(): void {
    // Try multiple times to ensure restoration works
    const attemptRestore = (attempt: number = 1) => {
      if (this.items.length > 0) {
        this.restoreSidebarState();
        this.isInitialized = true;
      } else if (attempt < 10) {
        setTimeout(() => attemptRestore(attempt + 1), 50);
      } else {
        this.isInitialized = true;
      }
    };

    attemptRestore();
  }

  /**
   * Save sidebar state to localStorage
   * Saves both top-level and intermediate-level (firstChild) expanded states
   */
  private saveSidebarState(): void {
    const isSidebarOpen = this.sidebarService.isOpen();

    const sidebarState: any = {
      sidebar: isSidebarOpen ? 1 : 0, // 0 = collapsed, 1 = expanded
      items: isSidebarOpen
        ? this.items.map((item) => {
            const itemState: any = {
              [item['name'] || item.label]: item.expanded ? 1 : 0, // 0 = collapsed, 1 = expanded
            };

            // Also save expanded state of intermediate items (firstChild level)
            if (item.items?.[0] && Array.isArray(item.items[0])) {
              const subItemsState: any = {};
              item.items[0].forEach((subItem: any) => {
                const subItemName = subItem['name'] || subItem.label;
                if (subItemName) {
                  subItemsState[subItemName] = subItem.expanded ? 1 : 0;
                }
              });
              // Store sub-items state under a special key
              if (Object.keys(subItemsState).length > 0) {
                itemState._subItems = subItemsState;
              }
            }

            return itemState;
          })
        : this.items.map((item) => ({
            [item['name'] || item.label]: 0, // All items = 0 when sidebar is collapsed
          })),
    };

    localStorage.setItem(this.SIDEBAR_STATE_KEY, JSON.stringify(sidebarState));
  }

  /**
   * Restore sidebar state from localStorage
   */
  private restoreSidebarState(): void {
    try {
      // Check new key first, then fallback to old key for migration
      const savedState =
        localStorage.getItem(this.SIDEBAR_STATE_KEY) ||
        localStorage.getItem('sidebar-status');
      if (savedState) {
        const sidebarState = JSON.parse(savedState);

        // Restore sidebar open state (on mobile responsive screens, respect initial auto-collapse)
        const shouldBeOpen = sidebarState.sidebar === 1;
        const isCurrentlyOpen = this.sidebarService.isOpen();

        if (
          shouldBeOpen !== isCurrentlyOpen &&
          !this.sidebarService.isResponsive()
        ) {
          this.sidebarService.toggle();
        }

        // Restore item expanded states - force expansion for items with status 1
        if (sidebarState.items && Array.isArray(sidebarState.items)) {
          sidebarState.items.forEach((itemState: any, index: number) => {
            const itemName = Object.keys(itemState).find(
              (key) => key !== '_subItems',
            );
            if (!itemName) return;

            const isExpanded = itemState[itemName] === 1;

            const menuItem = this.items.find(
              (item) => (item['name'] || item.label) === itemName,
            );

            if (menuItem) {
              menuItem.expanded = isExpanded;

              // Restore expanded state of intermediate items (firstChild level)
              if (
                itemState._subItems &&
                menuItem.items?.[0] &&
                Array.isArray(menuItem.items[0])
              ) {
                Object.keys(itemState._subItems).forEach((subItemName) => {
                  const subItemExpanded =
                    itemState._subItems[subItemName] === 1;
                  const subMenuItem = menuItem.items?.[0]?.find(
                    (subItem: any) =>
                      (subItem['name'] || subItem.label) === subItemName,
                  );
                  if (subMenuItem) {
                    subMenuItem.expanded = subItemExpanded;
                  }
                });
              }

              // Force change detection to update UI
              if (isExpanded) {
                // Force change detection
                this.cdr.detectChanges();
              }
            }
          });
        }

        // Force final change detection to ensure UI updates
        this.cdr.detectChanges();

        // Remove old key for migration
        localStorage.removeItem('sidebar-status');
      }
    } catch (error) {
      console.warn('❌ Failed to restore sidebar state:', error);
    }
  }

  /**
   * Expand the active menu item when sidebar is expanded
   * Handles 2-level and 3-level hierarchies
   */
  private expandActiveMenuItem(): void {
    const activated = this.activatedState();

    // Find the active parent menu item (top-level, e.g., "Settings")
    const activeParent = this.items.find((item) => {
      const itemName = item['name'] || item.label;
      return activated.parent === itemName;
    });

    if (
      activeParent &&
      activeParent.items &&
      activeParent.items[0] &&
      activeParent.items[0].length > 0
    ) {
      let stateChanged = false;

      // Expand the active parent menu item (top-level) only if not already expanded
      if (!activeParent.expanded) {
        activeParent.expanded = true;
        stateChanged = true;
      }

      // For 3-level hierarchies, also expand the intermediate parent (firstChild)
      // Example: Settings -> Product -> Traceability
      if (activated.firstChild && activeParent.items[0]) {
        const firstLevelItems = activeParent.items[0];
        if (Array.isArray(firstLevelItems)) {
          const intermediateParent = firstLevelItems.find((subItem: any) => {
            const subItemName = subItem['name'] || subItem.label;
            return activated.firstChild === subItemName;
          });

          if (intermediateParent && !intermediateParent.expanded) {
            // Expand the intermediate parent (e.g., "Product")
            intermediateParent.expanded = true;
            stateChanged = true;
          }
        }
      }

      // Also expand any intermediate parents in the hierarchy (fallback)
      this.expandParentHierarchy(activeParent);

      // Only save state to localStorage if something actually changed
      // This avoids unnecessary localStorage writes on every navigation
      if (stateChanged) {
        // Defer localStorage write to avoid blocking navigation
        requestAnimationFrame(() => {
          this.saveSidebarState();
        });
      }
    }
  }

  /**
   * Set up route tracking for sidebar activation
   */
  private setupRouteTracking(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);

        // Expand active menu item when route changes to preserve expanded state
        // This ensures that when navigating to a child route (e.g., Traceability),
        // the parent menus (Settings -> Product) remain expanded
        // Use requestAnimationFrame for better performance instead of setTimeout
        if (this.sidebarService.isOpen() && this.items.length > 0) {
          requestAnimationFrame(() => {
            this.expandActiveMenuItem();
            // Only save state if something actually changed
            // Don't force change detection - let Angular handle it naturally
          });
        }
      });

    // Set initial route
    this.currentRoute.set(this.router.url);
  }

  /**
   * Convert AuthService User to UserProfile format
   */
  private convertAuthUserToProfile(authUser: any): UserProfile {
    if (!authUser) {
      // Fallback profile if no user is authenticated
      return {
        id: '1',
        username: 'Guest User',
        email: 'guest@example.com',
        avatar: '',
        role: 'User',
        department: 'General',
        lastLogin: new Date(),
        isActive: true,
      };
    }

    return {
      id: authUser.id,
      username: authUser.username,
      email: authUser.email,
      avatar: authUser.avatar || '/assets/images/default-avatar.png',
      role: authUser.role,
      department: 'General',
      lastLogin: new Date(),
      isActive: true,
      firstName: authUser.firstName,
      lastName: authUser.lastName,
    };
  }

  /**
   * Load user info from AuthService with raw role (not translated)
   * Translation is handled reactively by translatedRole computed signal
   */
  private loadUserInfo(): void {
    const currentUser = this.authService.currentUser();
    if (currentUser) {
      const profile = this.convertAuthUserToProfile(currentUser);
      // Store raw role - translation is handled by translatedRole computed signal
      this.userInfo.set(profile);
    }
  }

  /**
   * Load user info from localStorage first, then Keycloak as fallback
   * Only loads if AuthService doesn't already have user data
   * Only makes API calls if token is expired or about to expire
   */
  private async loadUserInfoFromLocalStorage(): Promise<void> {
    // Skip if user is already loaded from AuthService to avoid duplicate API calls
    if (this.authService.currentUser()) {
      return;
    }

    // First, try to load from localStorage (userProfile)
    try {
      const storedUserProfile = localStorage.getItem('userProfile');
      if (storedUserProfile) {
        const profile: UserProfile = JSON.parse(storedUserProfile);
        // Validate that we have essential fields
        if (profile && profile.username && profile.email) {
          this.userInfo.set(profile);
          return; // Successfully loaded from localStorage, no need for API call
        }
      }
    } catch (error) {
      console.warn(
        '[Sidebar] Failed to parse userProfile from localStorage:',
        error,
      );
    }

    // If localStorage doesn't have userProfile, check if we need to call Keycloak API
    // Check if token is still valid before making API calls
    // If token is valid, skip API call to avoid unnecessary requests
    const isKeycloakAuthenticated = this.keycloakService.isAuthenticated();
    let isExpired = false;
    let tokenExpiry: number | null = null;

    if (isKeycloakAuthenticated) {
      isExpired = this.keycloakService.isTokenExpired();
      const tokenParsed = this.keycloakService.getTokenParsed();
      tokenExpiry = tokenParsed?.exp || null;
    } else {
      // Keycloak not yet authenticated (check-sso still in progress) — treat as expired to trigger sync
      isExpired = true;
    }

    let needsApiCall = isExpired;

    if (!isExpired && tokenExpiry) {
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenExpiry - now;
      // Only make API calls if token has less than 60 seconds left
      needsApiCall = timeUntilExpiry < 60;
    }

    // If token is still valid, skip API call (we already tried localStorage above)
    if (!needsApiCall) {
      return;
    }

    try {
      // Get user data from Keycloak directly (this is cached in KeycloakService)
      // Only called if token is expired/about to expire
      const userData = await this.keycloakService.getUserData();
      if (userData) {
        const profile: UserProfile = {
          id: userData.id,
          username: userData.username,
          email: userData.email,
          avatar: userData.avatar || '/assets/images/default-avatar.png',
          role: userData.role, // Store raw role - translation is handled by translatedRole computed signal
          department: 'General',
          lastLogin: new Date(userData.storedAt || Date.now()),
          isActive: true,
          firstName: userData.firstName,
          lastName: userData.lastName,
        };
        this.userInfo.set(profile);
        // Also save to localStorage for future use
        localStorage.setItem('userProfile', JSON.stringify(profile));
      }
    } catch (error) {
      console.error('[Sidebar] Failed to load user info from Keycloak:', error);
    }
  }

  /**
   * Translate user role
   */
  private translateUserRole(role: string): string {
    const roleKey = role.toLowerCase();
    switch (roleKey) {
      case 'administrator':
      case 'admin':
        return (
          this.translateService.instant('user_profile.roles.administrator') ||
          'Administrator'
        );
      case 'manager':
        return (
          this.translateService.instant('user_profile.roles.manager') ||
          'Manager'
        );
      case 'user':
        return (
          this.translateService.instant('user_profile.roles.user') || 'User'
        );
      default:
        return role;
    }
  }

  /**
   * Check if all children of a menu item are not implemented (no command property)
   */
  private areAllChildrenNotImplemented(menuItem: MegaMenuItem): boolean {
    if (!menuItem.items || menuItem.items.length === 0) {
      return false; // No children, so not applicable
    }

    // Flatten all children from all columns
    const allChildren: MenuItem[] = [];
    menuItem.items.forEach((column) => {
      if (Array.isArray(column)) {
        allChildren.push(...column);
      }
    });

    // Check if all children have no command (not implemented)
    return allChildren.every((child) => !child.command);
  }

  // Check if any submenu item is active and auto-expand parent
  checkAndExpandActiveMenus(): void {
    this.items.forEach((item) => {
      if (item.items?.[0]) {
        // Check if this parent has an active child (including deep nesting)
        const hasActiveSubItem = this.hasActiveChildRecursive(item, item);

        if (hasActiveSubItem) {
          item.expanded = true;
          // Also expand any intermediate parents in the hierarchy
          this.expandParentHierarchy(item);
        }
      }
    });
  }

  // Recursively check if a menu item has any active children
  private hasActiveChildRecursive(item: any, parentItem?: any): boolean {
    if (!item.items?.[0] || !Array.isArray(item.items[0])) return false;

    return item.items[0].some((subItem: any) => {
      // Check if this direct child is active (2nd level)
      if (this.isFirstChildActive(subItem, parentItem)) {
        return true;
      }

      // Recursively check deeper levels (3rd level)
      if (
        subItem.items &&
        Array.isArray(subItem.items) &&
        subItem.items.length > 0
      ) {
        return subItem.items.some((subSubItem: any) => {
          // Check if this 3rd level child is active
          if (this.isSecondChildActive(subSubItem, subItem, parentItem)) {
            return true;
          }
          // Recursively check even deeper levels if needed
          return this.hasActiveChildRecursive(subSubItem, subItem);
        });
      }

      return false;
    });
  }

  // Expand parent hierarchy to ensure active items are visible
  private expandParentHierarchy(item: any): void {
    // This method can be extended to handle more complex hierarchy expansion
    // For now, we just ensure the immediate parent is expanded
    if (item.expanded) {
      // If this item is expanded, also check its children
      if (item.items?.[0] && Array.isArray(item.items[0])) {
        item.items[0].forEach((subItem: any) => {
          if (
            subItem.items?.length > 0 &&
            this.hasActiveChildRecursive(subItem, subItem)
          ) {
            subItem.expanded = true;
          }
        });
      }
    }
  }

  private loadTranslations(): void {
    const cacheHeaders = { 'Cache-Control': 'no-cache', Pragma: 'no-cache' };
    // Load English translations
    this.http
      .get('./assets/i18n/en.json?v=20261030-mt3', { headers: cacheHeaders })
      .subscribe((translations: any) => {
        this.translateService.setTranslation('en', translations, true);

        // Load Vietnamese translations
        this.http
          .get('./assets/i18n/vi.json?v=20261030-mt3', { headers: cacheHeaders })
          .subscribe((viTranslations: any) => {
            this.translateService.setTranslation('vi', viTranslations, true);

            // Load user info with translated role
            this.loadUserInfo();

            // Now initialize menu items
            this.initializeMenuItems();
          });
      });
  }

  private canAccessMenuItem(item: {
    permissionCode?: string;
    route?: string;
    permission?: string;
    name: string;
  }): boolean {
    if (item.permissionCode) {
      return this.permissionService.canDoAction(item.permissionCode);
    }

    if (item.route) {
      return this.hasRoutePermission(item.route);
    }

    if (item.permission) {
      return this.hasMenuPermission({
        permission: item.permission,
        name: item.name,
      });
    }

    return true;
  }

  private initializeMenuItems(): void {
    try {
      // Initialize user menu items
      this.userMenuItems = [
        {
          label: 'sidebar.user_menu.profile',
          icon: 'pi pi-user',
          command: () => this.onUserProfile(),
        },
        {
          label: 'sidebar.user_menu.settings',
          icon: 'pi pi-cog',
          command: () => this.onUserSettings(),
        },
        {
          separator: true,
        },
        {
          label: 'sidebar.user_menu.sign_out',
          icon: 'pi pi-sign-out',
          command: () => this.onSignOut(),
        },
      ];

      // Get menu configuration from unified service
      const sidebarConfig = this.menuConfigService.getSidebarMenuConfig();

      if (!sidebarConfig || sidebarConfig.length === 0) {
        console.warn('⚠️ Sidebar: No menu config available');
        this.items = [];
        this.cdr.detectChanges();
        return;
      }

      // Preserve current expanded state before re-initializing
      // First try to get from current items, then from localStorage
      const expandedStateMap = new Map<string, boolean>();

      // Get from current items if available
      if (this.items && this.items.length > 0) {
        this.items.forEach((item) => {
          const itemName = item['name'] || item.label;
          if (itemName) {
            expandedStateMap.set(itemName, item.expanded || false);
          }
        });
      }

      // Also load from localStorage to ensure we have the saved state
      try {
        const savedState =
          localStorage.getItem(this.SIDEBAR_STATE_KEY) ||
          localStorage.getItem('sidebar-status');
        if (savedState) {
          const sidebarState = JSON.parse(savedState);
          if (sidebarState.items && Array.isArray(sidebarState.items)) {
            sidebarState.items.forEach((itemState: any) => {
              const itemName = Object.keys(itemState)[0];
              const isExpanded = itemState[itemName] === 1;
              // Only set if not already in map (current items take precedence)
              if (itemName && !expandedStateMap.has(itemName)) {
                expandedStateMap.set(itemName, isExpanded);
              }
            });
          }
        }
      } catch (error) {
        console.warn(
          '[Sidebar] Failed to load expanded state from localStorage:',
          error,
        );
      }

      // Convert to PrimeNG MegaMenu format and add navigation commands
      // Filter menu items based on user permissions
      const currentPermissions = this.permissionService.getCurrentPermissions();
      this.items = sidebarConfig
        .filter((section) => {
          const hasPermission = this.hasMenuPermission(section);
          return hasPermission;
        })
        .map((section) => {
          const itemName = section.name;
          // Restore expanded state from previous state if it existed
          const wasExpanded = expandedStateMap.get(itemName) || false;

          // Filter and map items - only include items user has permission to access
          const processedItems = section.items.map(
            (itemGroup) =>
              itemGroup
                .filter((item) => {
                  // Filter out items without permission
                  return this.canAccessMenuItem(item);
                })
                .map((item) => {
                  const menuItem: any = {
                    name: item.name,
                    label: item.label,
                    disabled: item.disabled, // Preserve disabled state
                    expanded: false, // Initialize expanded state for sub-items
                  };

                  // Add command if route exists and item is not disabled
                  if (item.route && !item.disabled) {
                    menuItem.command = () => this.navigateTo(item.route!);
                  }

                  // Handle 3-level hierarchy (Settings) - items already processed by UnifiedMenuConfigService
                  if (item.items && item.items.length > 0) {
                    // Filter children by permission, but preserve disabled state
                    const authorizedChildren = item.items
                      .filter((child: any) => {
                        // Check permission for this child
                        let hasPermission = false;
                        if (child.permissionCode) {
                          hasPermission = this.permissionService.canDoAction(
                            child.permissionCode,
                          );
                        } else if (child.route) {
                          hasPermission = this.hasRoutePermission(child.route);
                        } else if (child.permission) {
                          hasPermission = this.hasMenuPermission({
                            permission: child.permission,
                            name: child.name,
                          });
                        } else {
                          // If no permission specified, allow access (for backward compatibility)
                          hasPermission = true;
                        }
                        return hasPermission;
                      })
                      .map((child: any) => ({
                        name: child.name,
                        label: child.label,
                        disabled: child.disabled, // Preserve disabled state
                        expanded: false, // Initialize expanded state for 2nd level items
                        // Only add command if child has route and is not disabled
                        command:
                          child.route && !child.disabled
                            ? () => this.navigateTo(child.route!)
                            : undefined,
                      }));

                    // Only include this parent item if it has its own route or has authorized children
                    if (!item.route && authorizedChildren.length === 0) {
                      // Skip this item - no access
                      return null;
                    }

                    menuItem.items = authorizedChildren;
                  }

                  return menuItem;
                })
                .filter((item) => item !== null), // Remove null items (filtered out parents)
          );

          // Count total items after filtering
          const totalItems = processedItems.reduce(
            (sum, group) => sum + group.length,
            0,
          );

          return {
            name: itemName,
            label: section.label,
            icon: section.icon,
            root: section.root,
            disabled: section.disabled, // Include disabled flag from section
            expanded: wasExpanded, // Restore expanded state
            items: processedItems,
          };
        })
        .filter((section) => {
          // Filter out sections with no items
          const hasItems = section.items.some((group) => group.length > 0);
          return hasItems;
        });

      // Restore sidebar state after menu items are initialized
      // This ensures expanded states are preserved
      setTimeout(() => {
        this.restoreSidebarState();
        // Also expand active menu item if sidebar is open
        if (this.sidebarService.isOpen()) {
          this.expandActiveMenuItem();
        }
        this.cdr.detectChanges();
      }, 0);

      // Force change detection to ensure menu items are rendered
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Sidebar: Failed to initialize menu items:', error);
      this.items = [];
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if user has permission to view a menu section
   * @param section The menu section configuration
   * @returns true if user has READ permission for the section
   */
  private hasMenuPermission(section: {
    permission?: string;
    name?: string;
    route?: string;
  }): boolean {
    // If no permission is defined, allow access (for backward compatibility)
    // This allows sections like "Settings" to be visible

    if (!section.permission && !section.name) {
      return true;
    }

    // Get current permissions to check if they're loaded
    const currentPermissions = this.permissionService.getCurrentPermissions();

    // Wait for permissions to be loaded before showing menus
    // Check if permissions are loaded
    if (!this.permissionService.arePermissionsLoaded()) {
      // If permissions not loaded yet, don't show menus
      // The menu will refresh when permissions are loaded via subscription
      return false;
    }

    // Try new menu-based permission check first if menu name is available
    if (section.name) {
      // canViewMenu will handle menu name normalization internally
      // It uses mapMenuNameToModule to ensure consistent mapping
      // (e.g., "Third-Party" -> "third-party")
      // canViewMenu will return false if menu not in JSON config
      // Only menus defined in JSON will be shown
      return this.permissionService.canViewMenu(section.name);
    }

    // If no menu name and no permission, don't show (unless it's Settings)
    // Settings might not have permission defined, so we allow it for backward compatibility
    if (!section.permission) {
      // Only allow if it's a special section like Settings
      // Otherwise, require explicit permission definition
      return section.name === 'Settings' || section.name === 'settings';
    }

    // Check permission
    const hasPermission = this.permissionService.checkPermission(
      [section.permission],
      currentPermissions,
    );

    // Debug logging (can be removed in production)
    if (!hasPermission) {
      console.debug(
        `[Sidebar] Permission check failed. Section: ${
          section.name || 'Unknown'
        }, Required: ${section.permission}, User has: ${
          currentPermissions.length
        } permissions`,
      );
    }

    return hasPermission;
  }

  /**
   * Check if user has permission to access a route
   * @param route Route path
   * @returns true if user has view permission for this route
   */
  private hasRoutePermission(route: string): boolean {
    // Use new route-based permission check
    // This will return false if route is not in JSON config
    // Only routes defined in JSON will be shown
    return this.permissionService.canAccessRoute(route);
  }

  /**
   * Calculate smart position for floating panels to avoid viewport clipping
   * while maintaining sticky positioning with parent items
   */
  private calculateSmartPosition(
    targetRect: DOMRect,
    panelWidth: number = 250,
    panelHeight: number = 300,
    gap: number = 8,
  ): { x: number; y: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomPadding = 16; // Padding from bottom of viewport

    // Calculate initial position (to the right of target) - maintain sticky positioning
    let x = targetRect.right + gap;
    let y = targetRect.top;

    // Check if panel would overflow horizontally
    if (x + panelWidth > viewportWidth) {
      // Position to the left of target instead
      x = targetRect.left - panelWidth - gap;
    }

    // For vertical positioning, prioritize keeping panel connected to menu item
    const spaceBelow = viewportHeight - targetRect.bottom - bottomPadding;
    const spaceAbove = targetRect.top;

    // If there's enough space below, position normally
    if (spaceBelow >= panelHeight) {
      // Panel fits below, keep it aligned with top of menu item
      y = targetRect.top;
    } else if (spaceBelow > 0) {
      // Not enough space below, but some space exists
      // Stick panel bottom to menu item bottom to maintain hover connection
      y = targetRect.bottom - panelHeight;

      // If this would make panel go above viewport, adjust to fit
      if (y < 8) {
        y = 8;
      }

      // If panel would still overflow bottom, position it so bottom aligns with viewport bottom
      if (y + panelHeight > viewportHeight - bottomPadding) {
        y = viewportHeight - panelHeight - bottomPadding;
        // But ensure it doesn't go above viewport
        y = Math.max(8, y);
      }
    } else {
      // No space below, try to position above the target
      if (spaceAbove >= panelHeight) {
        // Position above the target if there's enough space above
        y = targetRect.top - panelHeight - gap;
        // Ensure it doesn't go above viewport
        y = Math.max(8, y);
      } else {
        // Not enough space above either, position at top of viewport
        // Panel will scroll if needed (handled by CSS)
        y = 8;
      }
    }

    // Ensure minimum Y position
    y = Math.max(8, y);

    return { x, y };
  }

  /**
   * Calculate smart position for collapsed sidebar panels
   * Special handling to maintain alignment with sidebar edge
   */
  private calculateCollapsedPanelPosition(
    targetRect: DOMRect,
    sidebarRect: DOMRect,
    panelWidth: number = 250,
    panelHeight: number = 300,
    gap: number = 8,
  ): { x: number; y: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomPadding = 16; // Padding from bottom of viewport

    // For collapsed sidebar, always position relative to sidebar edge
    let x = sidebarRect.right + gap;
    let y = targetRect.top;

    // Check if panel would overflow horizontally
    if (x + panelWidth > viewportWidth) {
      // Position to the left of sidebar instead
      x = sidebarRect.left - panelWidth - gap;
    }

    // For vertical positioning, prioritize keeping panel connected to menu item
    const spaceBelow = viewportHeight - targetRect.bottom - bottomPadding;
    const spaceAbove = targetRect.top;

    // If there's enough space below, position normally
    if (spaceBelow >= panelHeight) {
      // Panel fits below, keep it aligned with top of menu item
      y = targetRect.top;
    } else if (spaceBelow > 0) {
      // Not enough space below, but some space exists
      // Stick panel bottom to menu item bottom to maintain hover connection
      y = targetRect.bottom - panelHeight;

      // If this would make panel go above viewport, adjust to fit
      if (y < 8) {
        y = 8;
      }

      // If panel would still overflow bottom, position it so bottom aligns with viewport bottom
      if (y + panelHeight > viewportHeight - bottomPadding) {
        y = viewportHeight - panelHeight - bottomPadding;
        // But ensure it doesn't go above viewport
        y = Math.max(8, y);
      }
    } else {
      // No space below, try to position above the target
      if (spaceAbove >= panelHeight) {
        // Position above the target if there's enough space above
        y = targetRect.top - panelHeight - gap;
        // Ensure it doesn't go above viewport
        y = Math.max(8, y);
      } else {
        // Not enough space above either, position at top of viewport
        // Panel will scroll if needed (handled by CSS)
        y = 8;
      }
    }

    // Ensure minimum Y position
    y = Math.max(8, y);

    return { x, y };
  }

  onUserProfile(): void {
    this.navigateTo('/user/profile');
    this.isUserMenuOpen.set(false);
  }

  onUserSettings(): void {
    this.navigateTo('/user/settings');
    this.isUserMenuOpen.set(false);
  }

  onSignOut(): void {
    this.isUserMenuOpen.set(false);
    this.authService.logout();
  }

  handleMenuItemClick(item: MenuItem): void {
    if (item.command) {
      item.command({} as any);
    }

    // Hide all panels when clicking on any menu item
    this.hideAllPanels();

    // Auto-collapse mobile overlay drawer after selecting a leaf route
    if (this.sidebarService.isResponsive() && this.sidebarService.isOpen()) {
      this.sidebarService.collapse();
      this.saveSidebarState();
    }
  }

  expandIfClosed(event: Event): void {
    if (!this.sidebarService.isOpen()) {
      this.sidebarService.toggle();

      // Expand the active menu item after sidebar is expanded
      setTimeout(() => {
        this.expandActiveMenuItem();
      }, 100); // Small delay to ensure sidebar animation completes
    }
  }

  onToggle(): void {
    this.sidebarService.toggle();

    // Save sidebar state after toggle
    this.saveSidebarState();

    // If sidebar is now expanded, expand the active menu item
    if (this.sidebarService.isOpen()) {
      setTimeout(() => {
        this.expandActiveMenuItem();
      }, 100); // Small delay to ensure sidebar animation completes
    }
  }

  /**
   * Navigate to a path
   * @param path - The route path to navigate to
   */
  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  // Check if current route matches top-level menu item
  isMenuActive(item: any): boolean {
    if (!item) return false;

    const activated = this.activatedState();

    // Use name field if available, otherwise fall back to label
    const itemName = item['name'] || item.label;

    // Top-level menu is active if it's the current parent
    return activated.parent === itemName;
  }

  // Check if first-level child (like "File" under "Setting") is active
  isFirstChildActive(subItem: any, parentItem?: any): boolean {
    if (!subItem) return false;

    const activated = this.activatedState();

    // Use name field if available, otherwise fall back to label
    const subItemName = subItem['name'] || subItem.label;
    const parentName = parentItem?.['name'] || parentItem?.label;

    // First child is active if:
    // 1. It matches the firstChild in activated state
    // 2. Its parent matches the parent in activated state
    // 3. Either it's a 2-level route (no secondChild) OR it's part of a 3-level route
    const isActive =
      activated.parent === parentName && activated.firstChild === subItemName;

    return isActive;
  }

  // Check if second-level child (like "Upload Detail Configuration" under "File") is active
  isSecondChildActive(
    subSubItem: any,
    parentItem?: any,
    grandParentItem?: any,
  ): boolean {
    if (!subSubItem) return false;

    const activated = this.activatedState();

    // Use name field if available, otherwise fall back to label
    const subSubItemName = subSubItem['name'] || subSubItem.label;
    const parentName = parentItem?.['name'] || parentItem?.label;
    const grandParentName = grandParentItem?.['name'] || grandParentItem?.label;

    // Second child is active if all three levels match
    const isActive =
      activated.parent === grandParentName &&
      activated.firstChild === parentName &&
      activated.secondChild === subSubItemName;

    return isActive;
  }

  /**
   * Get user display name (firstName + lastName, fallback to username)
   */
  getDisplayName(): string {
    const userInfoValue = this.userInfo();
    if (!userInfoValue) {
      return 'Guest User';
    }

    // Use firstName + lastName if available
    if (userInfoValue.firstName || userInfoValue.lastName) {
      const parts = [userInfoValue.firstName, userInfoValue.lastName].filter(
        Boolean,
      );
      if (parts.length > 0) {
        return parts.join(' ').trim();
      }
    }

    // Fallback to username
    return userInfoValue.username || 'Guest User';
  }

  getInitials(): string {
    // Try to get from signal first
    const userInfoValue = this.userInfo();
    if (!userInfoValue) {
      return 'GU';
    }

    // Use firstName + lastName for initials if available
    if (userInfoValue.firstName || userInfoValue.lastName) {
      const parts = [userInfoValue.firstName, userInfoValue.lastName].filter(
        (part): part is string => part != null && part !== '',
      );
      if (parts.length > 0) {
        return parts
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
    }

    // Fallback to username
    if (userInfoValue.username) {
      return userInfoValue.username
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }

    // Fallback to AuthService
    const user = this.authService.currentUser();
    if (user) {
      // Use firstName + lastName if available
      if (user.firstName || user.lastName) {
        const parts = [user.firstName, user.lastName].filter(
          (part): part is string => part != null && part !== '',
        );
        if (parts.length > 0) {
          return parts
            .map((part) => part.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
        }
      }
      // Fallback to username
      if (user.username) {
        return user.username
          .split(' ')
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
    }

    // Final fallback
    return 'GU'; // Guest User fallback
  }

  // Helper method to check if menu item is active (has command)
  isMenuItemActive(item: any): boolean {
    return !!item.command;
  }

  // Helper method to normalize route by removing query params and fragments
  private normalizeRoute(path: string): string {
    return path.split('?')[0].split('#')[0];
  }

  // Helper method to check if route matches with better partial matching
  private routeMatches(route: string, currentRoute: string): boolean {
    const normalizedRoute = this.normalizeRoute(route);
    const normalizedCurrent = this.normalizeRoute(currentRoute);

    // Exact match
    if (normalizedCurrent === normalizedRoute) {
      return true;
    }

    // Partial match - ensure we match complete path segments
    if (normalizedCurrent.startsWith(normalizedRoute)) {
      const remaining = normalizedCurrent.substring(normalizedRoute.length);
      // Only match if the next character is '/' or we're at the end
      return remaining === '' || remaining.startsWith('/');
    }

    return false;
  }

  // Get current activated state based on route with 3-level hierarchy support
  getActivatedState(): {
    parent: string | null;
    firstChild: string | null;
    secondChild: string | null;
  } {
    const currentRoute = this.normalizeRoute(this.currentRoute());

    // Enhanced route mapping with 3-level hierarchy support
    const routeMap = this.menuConfigService.getRouteMapping();

    // Check for route matches using startsWith for flexibility
    // This allows for edit/add pages like /configuration/master-data/bpmn/edit/123 or /configuration/master-data/bpmn/add
    // Sort routes by path length (longest first) to match more specific routes first
    const sortedRouteMap = [...routeMap].sort(
      (a, b) => b.path.length - a.path.length,
    );
    const routeMatch = sortedRouteMap.find((route) => {
      if (currentRoute.startsWith(route.path)) {
        // Ensure we match complete path segments
        // If route path doesn't end with '/', check that next char is '/' or end of string
        const remaining = currentRoute.substring(route.path.length);
        return remaining === '' || remaining.startsWith('/');
      }
      return false;
    });
    if (routeMatch) {
      const result = {
        parent: routeMatch.parent,
        firstChild: routeMatch.firstChild,
        secondChild: routeMatch.secondChild || null,
      };
      return result;
    }

    // Default fallback - try to determine from route patterns
    if (currentRoute.startsWith('/channel')) {
      return { parent: 'Channel', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/pos')) {
      return { parent: 'POS', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/goods')) {
      return { parent: 'Product', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/stock')) {
      return { parent: 'Inventory', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/procurement')) {
      return { parent: 'Procurement', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/distribution')) {
      return { parent: 'Distribution', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/third')) {
      return { parent: 'Third-Party', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/order')) {
      return { parent: 'Standard Orders', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/o2o')) {
      return { parent: 'O2O Orders', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/after')) {
      return { parent: 'After-Sales', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/marketing')) {
      return { parent: 'Marketing', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/finance')) {
      return { parent: 'Finance', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/build')) {
      return { parent: 'Build', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/logistics')) {
      return { parent: 'Logistics', firstChild: null, secondChild: null };
    }
    if (currentRoute.startsWith('/report')) {
      return { parent: 'Report', firstChild: null, secondChild: null };
    }
    // Only match Settings if we're on specific setting routes, not general configuration
    if (currentRoute.startsWith('/configuration/import')) {
      return { parent: 'Settings', firstChild: 'Import', secondChild: null };
    }
    if (currentRoute.startsWith('/configuration/file')) {
      return { parent: 'Settings', firstChild: 'File', secondChild: null };
    }
    if (currentRoute.startsWith('/configuration/organization')) {
      return {
        parent: 'Settings',
        firstChild: 'Organizational Structure',
        secondChild: null,
      };
    }
    if (currentRoute.startsWith('/configuration/master-data/bpmn')) {
      const result = {
        parent: 'Settings',
        firstChild: 'Master Data',
        secondChild: 'Workflow Design',
      };
      return result;
    }
    if (currentRoute.startsWith('/configuration/master-data/tag-management')) {
      return {
        parent: 'Settings',
        firstChild: 'Master Data',
        secondChild: 'Label Management',
      };
    }
    if (currentRoute.startsWith('/configuration/upload-detail')) {
      // Development-only debug logging
      return {
        parent: 'Settings',
        firstChild: 'File',
        secondChild: 'Upload Detail Configuration',
      };
    }
    if (
      currentRoute.startsWith('/configuration/finance/payment-configuration')
    ) {
      return {
        parent: 'Settings',
        firstChild: 'Finance',
        secondChild: 'Payment Configuration',
      };
    }
    if (currentRoute.startsWith('/configuration/order/')) {
      return { parent: 'Settings', firstChild: 'Order', secondChild: null };
    }
    if (
      currentRoute.startsWith('/configuration') ||
      currentRoute.startsWith('/configuration-center') ||
      currentRoute.startsWith('/workflow')
    ) {
      return { parent: 'Settings', firstChild: null, secondChild: null };
    }

    return { parent: null, firstChild: null, secondChild: null };
  }

  // Helper method to check if we're in development mode
  private isDevelopmentMode(): boolean {
    return !environment.production;
  }

  // Toggle main menu item
  toggleMenuItem(item: any): void {
    if (this.sidebarService.isOpen()) {
      item.expanded = !item.expanded;
      // Save state to localStorage
      this.saveSidebarState();
    } else {
      // If sidebar is collapsed, expand it first
      this.sidebarService.toggle();
      // Then expand the menu item after a short delay
      setTimeout(() => {
        item.expanded = true;
        // Save state to localStorage
        this.saveSidebarState();
      }, 100);
    }

    // Hide all panels when toggling menu items
    this.hideAllPanels();
  }

  // Toggle submenu item
  toggleSubMenuItem(subItem: any): void {
    subItem.expanded = !subItem.expanded;

    // Save state to localStorage
    this.saveSidebarState();

    // Hide all panels when toggling submenu items
    this.hideAllPanels();
  }

  // Handle menu item hover (for collapsed sidebar)
  onMenuItemHover(item: any, event: MouseEvent): void {
    // Prevent hover interference during initialization
    if (!this.isInitialized) {
      return;
    }

    // Set sidebar hover state
    this.isHoveringSidebar = true;

    // Clear any existing timeout to prevent race conditions
    if (this.menuItemTimeout) {
      clearTimeout(this.menuItemTimeout);
      this.menuItemTimeout = null;
    }

    if (this.panelHideTimeout) {
      clearTimeout(this.panelHideTimeout);
      this.panelHideTimeout = null;
    }

    if (!this.sidebarService.isOpen()) {
      // Hide previous panels if hovering on a different item
      if (
        this.collapsedPanelHoveredItem &&
        this.collapsedPanelHoveredItem !== item
      ) {
        this.hideAllPanels();
      }

      item.showFloatingMenu = true;

      // Show collapsed panel for all items when sidebar is collapsed
      this.showCollapsedPanel(item, event);
    } else if (item.expanded) {
      // If item is already expanded, do nothing on hover
      // Don't hide panels or show new ones - just maintain current state
      return;
    } else if (item.items?.[0]?.length > 0) {
      // Hide previous panels if hovering on a different item
      const currentHoveredItem = this.firstLevelHoveredItem || this.hoveredItem;
      if (currentHoveredItem && currentHoveredItem !== item) {
        this.hideAllPanels();
      }

      // Only show panels if the item is not expanded
      // Special handling for Settings menu - show first level panel
      if (item.label === 'sidebar.navigation.setting') {
        this.showFirstLevelPanel(item, event);
      } else {
        // Show tiered menu for other items
        this.showTieredMenu(item, event);
      }
    }
  }

  // Handle menu item leave (for both collapsed and expanded sidebar)
  onMenuItemLeave(item: any, event: MouseEvent): void {
    // Prevent hover interference during initialization
    if (!this.isInitialized) {
      return;
    }

    // Clear any existing timeout to prevent race conditions
    if (this.menuItemTimeout) {
      clearTimeout(this.menuItemTimeout);
      this.menuItemTimeout = null;
    }

    if (this.panelHideTimeout) {
      clearTimeout(this.panelHideTimeout);
      this.panelHideTimeout = null;
    }

    if (!this.sidebarService.isOpen()) {
      // Clear any existing timeout
      if (this.collapsedPanelTimeout) {
        clearTimeout(this.collapsedPanelTimeout);
        this.collapsedPanelTimeout = null;
      }

      // Rule 2: If user leaves sidebar and 1st panel without selecting → close 1st panel
      // Rule 4: If user leaves both panels and sidebar → close all panels
      this.collapsedPanelTimeout = setTimeout(() => {
        // Only hide if not hovering sidebar or any panels
        if (
          !this.isHoveringSidebar &&
          !this.isHoveringCollapsedPanel &&
          !this.isHoveringCollapsedSecondLevelPanel
        ) {
          item.showFloatingMenu = false;
          this.collapsedPanelVisible = false;
          this.collapsedPanelHoveredItem = null;
          this.collapsedSecondLevelPanelVisible = false;
          this.collapsedSecondLevelHoveredItem = null;
        }
        this.collapsedPanelTimeout = null;
      }, 100); // Shorter delay for collapsed sidebar

      // Hide hover panel
      this.hoveredItem = null;
    } else {
      // Reset sidebar hover state when leaving menu item (expanded sidebar)
      this.isHoveringSidebar = false;

      // Don't hide panels if the item is already expanded (maintain expanded state)
      if (item.expanded) {
        return; // Don't interfere with expanded items
      }

      // For expanded sidebar, use consistent hover logic with collapsed sidebar
      this.menuItemTimeout = setTimeout(() => {
        // Check if we're still not hovering over sidebar or any panels
        if (
          !this.isHoveringSidebar &&
          !this.isHoveringPanel &&
          !this.isHoveringFirstLevelPanel &&
          !this.isHoveringSecondLevelPanel
        ) {
          // Hide all panels consistently
          this.tieredMenuVisible = false;
          this.hoveredItem = null;
          this.firstLevelPanelVisible = false;
          this.firstLevelHoveredItem = null;
          this.secondLevelPanelVisible = false;
          this.secondLevelHoveredItem = null;
        }
        this.menuItemTimeout = null;
      }, 100); // Same delay as collapsed sidebar for consistency
    }
  }

  // Handle sidebar leave - reset sidebar hover state
  onSidebarLeave(): void {
    this.isHoveringSidebar = false;

    // Clear any existing timeout
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    // Rule 4: If user leaves both panels and sidebar → close all panels
    // For both collapsed and expanded sidebar, be aggressive about hiding panels
    if (!this.sidebarService.isOpen()) {
      // Immediately hide collapsed panels when leaving sidebar
      this.collapsedPanelVisible = false;
      this.collapsedPanelHoveredItem = null;
      this.collapsedSecondLevelPanelVisible = false;
      this.collapsedSecondLevelHoveredItem = null;
    } else {
      // For expanded sidebar, also be aggressive about hiding panels
      this.tieredMenuVisible = false;
      this.hoveredItem = null;
      this.firstLevelPanelVisible = false;
      this.firstLevelHoveredItem = null;
      this.secondLevelPanelVisible = false;
      this.secondLevelHoveredItem = null;
    }
  }

  // Handle floating menu hover
  onFloatingMenuHover(item: any): void {
    item.showFloatingMenu = true;
  }

  // Update hover panel position based on mouse event
  updateHoverPanelPosition(event: MouseEvent): void {
    const sidebarWidth = 56; // Width of collapsed sidebar (w-14 = 56px)
    const panelOffset = 8; // Small gap between sidebar and panel

    this.hoverPanelPosition = {
      x: sidebarWidth + panelOffset,
      y: (event.currentTarget as HTMLElement)?.offsetTop ?? 0,
    };
  }

  // Show tiered menu for collapsed sidebar items
  showTieredMenu(item: any, event: MouseEvent): void {
    // Don't show panel if item is expanded
    if (item.expanded) {
      return;
    }

    if (item.items?.[0]?.length > 0) {
      // Hide other panels first to ensure clean state
      if (this.firstLevelHoveredItem) {
        this.firstLevelPanelVisible = false;
        this.firstLevelHoveredItem = null;
      }
      if (this.secondLevelPanelVisible) {
        this.secondLevelPanelVisible = false;
        this.secondLevelHoveredItem = null;
      }

      // Set the hovered item
      this.hoveredItem = item;

      // Calculate position with smart viewport handling
      const target = event.currentTarget as HTMLElement;
      const rect = target?.getBoundingClientRect();
      const itemCount = Array.isArray(item.items?.[0])
        ? item.items[0].length
        : 0;
      const estimatedHeight = itemCount * 36 + 24; // More accurate height calculation

      const position = this.calculateSmartPosition(
        rect,
        250,
        estimatedHeight,
        8,
      );

      this.tieredMenuPosition = {
        x: position.x,
        y: position.y,
      };

      this.tieredMenuVisible = true;
    }
  }

  // Handle floating menu leave
  onFloatingMenuLeave(item: any): void {
    item.showFloatingMenu = false;
  }

  // Handle panel hover - keep panel open when hovering over it
  onPanelHover(): void {
    // Clear any existing timeout to prevent race conditions
    if (this.menuItemTimeout) {
      clearTimeout(this.menuItemTimeout);
      this.menuItemTimeout = null;
    }

    // Clear fallback timeout
    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }

    // Keep the panel open when hovering over it
    this.isHoveringPanel = true;
  }

  // Handle panel leave - hide panel when leaving it
  onPanelLeave(): void {
    // Clear any existing timeout to prevent race conditions
    if (this.menuItemTimeout) {
      clearTimeout(this.menuItemTimeout);
      this.menuItemTimeout = null;
    }

    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }

    this.isHoveringPanel = false;

    // Immediately clear the hovered item to hide the panel
    this.hoveredItem = null;

    // Add a small delay to allow moving between elements
    this.menuItemTimeout = setTimeout(() => {
      // Check if we're still not hovering over any panel or sidebar
      if (
        !this.isHoveringPanel &&
        !this.isHoveringFirstLevelPanel &&
        !this.isHoveringSecondLevelPanel &&
        !this.isHoveringSidebar
      ) {
        // Hide all panels consistently
        this.tieredMenuVisible = false;
        this.firstLevelPanelVisible = false;
        this.firstLevelHoveredItem = null;
        this.secondLevelPanelVisible = false;
        this.secondLevelHoveredItem = null;
      }
      this.menuItemTimeout = null;
    }, 100); // Small delay for smooth hover experience

    // Set a fallback timeout to ensure panels are hidden after maximum time
    this.panelHideFallbackTimeout = setTimeout(() => {
      this.hideAllPanels();
      this.panelHideFallbackTimeout = null;
    }, 2000); // Maximum 2 seconds before forcing hide
  }

  // Show first level panel for Settings menu
  showFirstLevelPanel(item: any, event: MouseEvent): void {
    // Don't show panel if item is expanded
    if (item.expanded) {
      return;
    }

    if (item.items?.[0]?.length > 0) {
      // Hide other panels first to ensure clean state
      if (this.hoveredItem) {
        this.tieredMenuVisible = false;
        this.hoveredItem = null;
      }
      if (this.secondLevelPanelVisible) {
        this.secondLevelPanelVisible = false;
        this.secondLevelHoveredItem = null;
      }

      this.firstLevelHoveredItem = item;

      // Calculate position with smart viewport handling
      const target = event.currentTarget as HTMLElement;
      const rect = target?.getBoundingClientRect();
      const itemCount = Array.isArray(item.items?.[0])
        ? item.items[0].length
        : 0;
      const estimatedHeight = itemCount * 36 + 24; // More accurate height calculation

      const position = this.calculateSmartPosition(
        rect,
        250,
        estimatedHeight,
        8,
      );

      this.firstLevelPanelPosition = {
        x: position.x,
        y: position.y,
      };

      this.firstLevelPanelVisible = true;
    }
  }

  // Show second level panel for items with children
  showSecondLevelPanel(subItem: any, event: MouseEvent): void {
    if (subItem.items?.length > 0) {
      this.secondLevelHoveredItem = subItem;

      // Calculate position with smart viewport handling
      const target = event.currentTarget as HTMLElement;
      const rect = target?.getBoundingClientRect();
      const itemCount = subItem.items.length;
      const estimatedHeight = itemCount * 36 + 24; // More accurate height calculation

      const position = this.calculateSmartPosition(
        rect,
        250,
        estimatedHeight,
        8,
      );

      this.secondLevelPanelPosition = {
        x: position.x,
        y: position.y,
      };

      this.secondLevelPanelVisible = true;
    }
  }

  // Handle first level panel hover
  onFirstLevelPanelHover(): void {
    this.isHoveringFirstLevelPanel = true;

    // Clear any timeout that might hide the panel
    if (this.firstLevelTimeout) {
      clearTimeout(this.firstLevelTimeout);
      this.firstLevelTimeout = null;
    }

    // Clear fallback timeout
    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }
  }

  // Handle first level panel leave
  onFirstLevelPanelLeave(): void {
    // Clear any existing timeout to prevent race conditions
    if (this.firstLevelTimeout) {
      clearTimeout(this.firstLevelTimeout);
      this.firstLevelTimeout = null;
    }

    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }

    this.isHoveringFirstLevelPanel = false;

    // Immediately clear the hovered item to hide the panel
    this.firstLevelHoveredItem = null;

    // Add delay before hiding to allow moving to second level panel
    this.firstLevelTimeout = setTimeout(() => {
      // Check if we're still not hovering over any panel or sidebar
      if (
        !this.isHoveringFirstLevelPanel &&
        !this.isHoveringSecondLevelPanel &&
        !this.isHoveringPanel &&
        !this.isHoveringSidebar
      ) {
        // Hide all panels consistently
        this.firstLevelPanelVisible = false;
        this.secondLevelPanelVisible = false;
        this.secondLevelHoveredItem = null;
        this.tieredMenuVisible = false;
        this.hoveredItem = null;
      }
      this.firstLevelTimeout = null;
    }, 150); // Reasonable delay for smooth hover experience

    // Set a fallback timeout to ensure panels are hidden after maximum time
    this.panelHideFallbackTimeout = setTimeout(() => {
      this.hideAllPanels();
      this.panelHideFallbackTimeout = null;
    }, 2000); // Maximum 2 seconds before forcing hide
  }

  // Handle second level panel hover
  onSecondLevelPanelHover(): void {
    this.isHoveringSecondLevelPanel = true;

    // Clear any timeouts that might hide the panels
    if (this.firstLevelTimeout) {
      clearTimeout(this.firstLevelTimeout);
      this.firstLevelTimeout = null;
    }
    if (this.secondLevelTimeout) {
      clearTimeout(this.secondLevelTimeout);
      this.secondLevelTimeout = null;
    }
  }

  // Handle second level panel leave
  onSecondLevelPanelLeave(): void {
    // Clear any existing timeout to prevent race conditions
    if (this.secondLevelTimeout) {
      clearTimeout(this.secondLevelTimeout);
      this.secondLevelTimeout = null;
    }

    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }

    this.isHoveringSecondLevelPanel = false;

    // Immediately clear the hovered item to hide the panel
    this.secondLevelHoveredItem = null;

    // Add delay before hiding
    this.secondLevelTimeout = setTimeout(() => {
      // Check if we're still not hovering over any panel or sidebar
      if (
        !this.isHoveringSecondLevelPanel &&
        !this.isHoveringFirstLevelPanel &&
        !this.isHoveringPanel &&
        !this.isHoveringSidebar
      ) {
        // Hide all panels consistently
        this.secondLevelPanelVisible = false;
        this.firstLevelPanelVisible = false;
        this.firstLevelHoveredItem = null;
        this.tieredMenuVisible = false;
        this.hoveredItem = null;
      }
      this.secondLevelTimeout = null;
    }, 100); // Reasonable delay for smooth hover experience

    // Set a fallback timeout to ensure panels are hidden after maximum time
    this.panelHideFallbackTimeout = setTimeout(() => {
      this.hideAllPanels();
      this.panelHideFallbackTimeout = null;
    }, 2000); // Maximum 2 seconds before forcing hide
  }

  // Handle first level item hover
  onFirstLevelItemHover(subItem: any, event: MouseEvent): void {
    // Clear any existing timeouts
    if (this.firstLevelTimeout) {
      clearTimeout(this.firstLevelTimeout);
      this.firstLevelTimeout = null;
    }
    if (this.secondLevelTimeout) {
      clearTimeout(this.secondLevelTimeout);
      this.secondLevelTimeout = null;
    }

    // If this item has children, show second level panel
    if (subItem.items?.length > 0) {
      this.showSecondLevelPanel(subItem, event);
    } else {
      // Hide second level panel if this item has no children
      this.secondLevelPanelVisible = false;
      this.secondLevelHoveredItem = null;
    }
  }

  // Handle first level item leave
  onFirstLevelItemLeave(subItem: any): void {
    // Clear any existing timeout to prevent race conditions
    if (this.secondLevelTimeout) {
      clearTimeout(this.secondLevelTimeout);
      this.secondLevelTimeout = null;
    }

    // Add delay to allow moving to second level panel
    this.secondLevelTimeout = setTimeout(() => {
      if (!this.isHoveringSecondLevelPanel) {
        this.secondLevelPanelVisible = false;
        this.secondLevelHoveredItem = null;

        // Also check if we should hide the first level panel
        // This handles the case where user leaves the first level item
        // and there's no second level panel to move to
        if (!this.isHoveringFirstLevelPanel) {
          this.firstLevelPanelVisible = false;
          this.firstLevelHoveredItem = null;
        }
      }
      this.secondLevelTimeout = null;
    }, 200);
  }

  // Handle submenu item hover (for both collapsed and expanded sidebar)
  onSubMenuItemHover(subItem: any, parentItem: any, event: MouseEvent): void {
    if (!this.sidebarService.isOpen()) {
      // Show hover panel if subItem has children (collapsed sidebar)
      if (subItem.items?.length > 0) {
        this.hoveredItem = subItem;
        this.updateHoverPanelPosition(event);
      }
    } else {
      // Handle expanded sidebar - show two-panel structure for sub-items with children
      if (subItem.items?.length > 0 && !subItem.expanded) {
        this.showFirstLevelPanel(subItem, event);
      }
    }
  }

  // Handle submenu item leave (for both collapsed and expanded sidebar)
  onSubMenuItemLeave(subItem: any, event: MouseEvent): void {
    if (!this.sidebarService.isOpen()) {
      // Hide hover panel (collapsed sidebar)
      this.hoveredItem = null;
    } else {
      // For expanded sidebar, check if we should hide panels
      // Clear any existing timeout to prevent race conditions
      if (this.menuItemTimeout) {
        clearTimeout(this.menuItemTimeout);
        this.menuItemTimeout = null;
      }

      // Add a small delay to allow moving to other elements
      this.menuItemTimeout = setTimeout(() => {
        // If not hovering over any panel or sidebar, hide all panels
        if (
          !this.isHoveringFirstLevelPanel &&
          !this.isHoveringSecondLevelPanel &&
          !this.isHoveringPanel &&
          !this.isHoveringSidebar
        ) {
          // Hide all panels consistently
          this.firstLevelPanelVisible = false;
          this.firstLevelHoveredItem = null;
          this.secondLevelPanelVisible = false;
          this.secondLevelHoveredItem = null;
          this.tieredMenuVisible = false;
          this.hoveredItem = null;
        }
        this.menuItemTimeout = null;
      }, 150);
    }
  }

  // Check if first level panel item is active
  isFirstLevelPanelItemActive(subItem: any): boolean {
    const activated = this.activatedState();
    if (!activated) return false;

    // Use name field if available, otherwise fall back to label
    const subItemName = subItem['name'] || subItem.label;
    const isActive = activated.firstChild === subItemName;

    return isActive;
  }

  // Check if second level panel item is active
  isSecondLevelPanelItemActive(subSubItem: any): boolean {
    const activated = this.activatedState();
    if (!activated) return false;

    // Use name field if available, otherwise fall back to label
    const subSubItemName = subSubItem['name'] || subSubItem.label;
    const isActive = activated.secondChild === subSubItemName;

    return isActive;
  }

  // Check if regular panel item is active (for non-Settings panels)
  isPanelItemActive(panelItem: any): boolean {
    const activated = this.activatedState();
    if (!activated) return false;

    // Use name field if available, otherwise fall back to label
    const panelItemName = panelItem['name'] || panelItem.label;

    // For regular panels, check if the item matches the firstChild in activated state
    return activated.firstChild === panelItemName;
  }

  // Hide all panels
  hideAllPanels(): void {
    // Clear all timeouts
    if (this.firstLevelTimeout) {
      clearTimeout(this.firstLevelTimeout);
      this.firstLevelTimeout = null;
    }
    if (this.secondLevelTimeout) {
      clearTimeout(this.secondLevelTimeout);
      this.secondLevelTimeout = null;
    }
    if (this.menuItemTimeout) {
      clearTimeout(this.menuItemTimeout);
      this.menuItemTimeout = null;
    }
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }
    if (this.globalMouseLeaveTimeout) {
      clearTimeout(this.globalMouseLeaveTimeout);
      this.globalMouseLeaveTimeout = null;
    }
    if (this.panelHideFallbackTimeout) {
      clearTimeout(this.panelHideFallbackTimeout);
      this.panelHideFallbackTimeout = null;
    }

    // Hide all panels
    this.tieredMenuVisible = false;
    this.hoveredItem = null;

    this.firstLevelPanelVisible = false;
    this.firstLevelHoveredItem = null;

    this.secondLevelPanelVisible = false;
    this.secondLevelHoveredItem = null;

    this.collapsedPanelVisible = false;
    this.collapsedPanelHoveredItem = null;

    this.collapsedSecondLevelPanelVisible = false;
    this.collapsedSecondLevelHoveredItem = null;

    // Reset hover states
    this.isHoveringPanel = false;
    this.isHoveringFirstLevelPanel = false;
    this.isHoveringSecondLevelPanel = false;
    this.isHoveringCollapsedSecondLevelPanel = false;
    this.isHoveringCollapsedPanel = false;
  }

  // Handle logo area hover - hide all panels when hovering over logo
  onLogoHover(): void {
    this.isLogoHovered = true;
    this.hideAllPanels();
  }

  // Handle logo area leave
  onLogoLeave(): void {
    this.isLogoHovered = false;
  }

  // Show collapsed sidebar panel
  showCollapsedPanel(item: any, event: MouseEvent): void {
    // Hide other panels first if hovering on a different item
    if (
      this.collapsedPanelHoveredItem &&
      this.collapsedPanelHoveredItem !== item
    ) {
      this.collapsedPanelVisible = false;
      this.collapsedSecondLevelPanelVisible = false;
      this.collapsedSecondLevelHoveredItem = null;
    }

    this.collapsedPanelHoveredItem = item;

    // Calculate position with smart viewport handling
    const target = event.currentTarget as HTMLElement;
    const rect = target?.getBoundingClientRect();
    const sidebarElement = target.closest('nav');
    const sidebarRect = sidebarElement?.getBoundingClientRect() || rect;

    const itemCount = Array.isArray(item.items?.[0]) ? item.items[0].length : 0;
    const estimatedHeight = itemCount * 36 + 24; // More accurate height calculation

    const position = this.calculateCollapsedPanelPosition(
      rect,
      sidebarRect,
      250,
      estimatedHeight,
      0, // No gap for collapsed sidebar
    );

    this.collapsedPanelPosition = {
      x: position.x,
      y: position.y,
    };

    this.collapsedPanelVisible = true;
  }

  // Handle collapsed panel hover
  onCollapsedPanelHover(): void {
    // Set panel hover states
    this.isHoveringCollapsedPanel = true;
    this.isHoveringAnyPanel = true;

    // Rule 1: When hovering sidebar icon, show 1st panel
    this.collapsedPanelVisible = true;

    // Clear any existing timeout to prevent hiding the panel
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    if (this.panelHideTimeout) {
      clearTimeout(this.panelHideTimeout);
      this.panelHideTimeout = null;
    }
  }

  // Handle collapsed panel leave
  onCollapsedPanelLeave(): void {
    this.isHoveringCollapsedPanel = false;

    // Reset isHoveringAnyPanel if not hovering any other panels
    if (!this.isHoveringCollapsedSecondLevelPanel) {
      this.isHoveringAnyPanel = false;
    }

    // Clear any existing timeout
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    // Rule 2: If user leaves sidebar and 1st panel without selecting → close 1st panel
    // Rule 4: If user leaves both panels and sidebar → close all panels
    // For collapsed sidebar, be more aggressive about hiding panels
    this.collapsedPanelTimeout = setTimeout(() => {
      // Check if we're still not hovering over sidebar or any panels
      if (
        !this.isHoveringSidebar &&
        !this.isHoveringCollapsedPanel &&
        !this.isHoveringCollapsedSecondLevelPanel
      ) {
        this.collapsedPanelVisible = false;
        this.collapsedPanelHoveredItem = null;
        this.collapsedSecondLevelPanelVisible = false;
        this.collapsedSecondLevelHoveredItem = null;
      }
      this.collapsedPanelTimeout = null;
    }, 100); // Shorter delay for collapsed sidebar
  }

  // Collapsed second level panel methods
  onCollapsedFirstLevelItemHover(subItem: any, event: MouseEvent): void {
    if (!subItem.items || subItem.items.length === 0) {
      return;
    }

    // Rule 3: If the hovered item in 1st panel has child, open 2nd panel
    // Set panel hover states
    this.isHoveringCollapsedSecondLevelPanel = true;
    this.isHoveringAnyPanel = true;

    this.collapsedSecondLevelHoveredItem = subItem;

    // Calculate position with smart viewport handling
    const target = event.target as HTMLElement;
    const rect = target?.getBoundingClientRect();
    const itemCount = subItem.items.length;
    const estimatedHeight = itemCount * 36 + 24; // More accurate height calculation

    const position = this.calculateSmartPosition(
      rect,
      250,
      estimatedHeight,
      0, // No gap for collapsed sidebar
    );

    this.collapsedSecondLevelPanelPosition = {
      x: position.x,
      y: position.y,
    };

    this.collapsedSecondLevelPanelVisible = true;

    // Clear any existing timeout to prevent hiding panels
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }
  }

  onCollapsedFirstLevelItemLeave(subItem: any): void {
    // Clear any existing timeout to prevent race conditions
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    // Rule 3: Keep both 1st and 2nd panel visible while mouse is on either
    this.collapsedPanelTimeout = setTimeout(() => {
      // Only hide 2nd panel if not hovering it
      if (!this.isHoveringCollapsedSecondLevelPanel) {
        this.collapsedSecondLevelPanelVisible = false;
        this.collapsedSecondLevelHoveredItem = null;
      }
      this.collapsedPanelTimeout = null;
    }, 200); // Rule 3: Reasonable delay for smooth transitions
  }

  onCollapsedSecondLevelPanelHover(): void {
    // Rule 3: Keep both 1st and 2nd panel visible while mouse is on either
    // Set panel hover states
    this.isHoveringCollapsedSecondLevelPanel = true;
    this.isHoveringAnyPanel = true;

    // Clear any existing timeout to prevent hiding panels
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    if (this.panelHideTimeout) {
      clearTimeout(this.panelHideTimeout);
      this.panelHideTimeout = null;
    }
  }

  onCollapsedSecondLevelPanelLeave(): void {
    this.isHoveringCollapsedSecondLevelPanel = false;

    // Reset isHoveringAnyPanel if not hovering any other panels
    if (!this.isHoveringCollapsedPanel) {
      this.isHoveringAnyPanel = false;
    }

    // Clear any existing timeout
    if (this.collapsedPanelTimeout) {
      clearTimeout(this.collapsedPanelTimeout);
      this.collapsedPanelTimeout = null;
    }

    // Rule 3: If user leaves 2nd panel but returns to 1st → close 2nd panel, keep 1st open
    // Rule 4: If user leaves both panels and sidebar → close all panels
    this.collapsedPanelTimeout = setTimeout(() => {
      // Check if we're still not hovering over sidebar or any panels
      if (
        !this.isHoveringSidebar &&
        !this.isHoveringCollapsedPanel &&
        !this.isHoveringCollapsedSecondLevelPanel
      ) {
        // Rule 4: User left both panels and sidebar → close all panels
        this.collapsedPanelVisible = false;
        this.collapsedPanelHoveredItem = null;
        this.collapsedSecondLevelPanelVisible = false;
        this.collapsedSecondLevelHoveredItem = null;
      } else if (
        this.isHoveringCollapsedPanel &&
        !this.isHoveringCollapsedSecondLevelPanel
      ) {
        // Rule 3: User returned to 1st panel → close 2nd panel, keep 1st open
        this.collapsedSecondLevelPanelVisible = false;
        this.collapsedSecondLevelHoveredItem = null;
      }
      this.collapsedPanelTimeout = null;
    }, 100); // Shorter delay for collapsed sidebar
  }

  toTestId(value: unknown): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Setup global mouse tracking for better hover detection
   */
  private setupGlobalMouseTracking(): void {
    // Track mouse movement globally to detect when mouse leaves sidebar area
    document.addEventListener('mousemove', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sidebarElement = target.closest('nav');
      const panelElement = target.closest(
        '.panel-container, .floating-menu, .p-tieredmenu',
      );

      // Update global tracking states
      this.isMouseOverSidebar = !!sidebarElement;
      this.isMouseOverAnyPanel = !!panelElement;

      // Clear any existing global timeout
      if (this.globalMouseLeaveTimeout) {
        clearTimeout(this.globalMouseLeaveTimeout);
        this.globalMouseLeaveTimeout = null;
      }

      // If mouse is not over sidebar or any panel, hide all panels after a short delay
      // Apply to both collapsed and expanded sidebar for consistency
      if (!this.isMouseOverSidebar && !this.isMouseOverAnyPanel) {
        // Set a timeout to hide panels if mouse doesn't return
        this.globalMouseLeaveTimeout = setTimeout(() => {
          if (!this.isMouseOverSidebar && !this.isMouseOverAnyPanel) {
            this.hideAllPanels();
            // Reset all hover states for consistency
            this.isHoveringSidebar = false;
            this.isHoveringPanel = false;
            this.isHoveringFirstLevelPanel = false;
            this.isHoveringSecondLevelPanel = false;
            this.isHoveringCollapsedPanel = false;
            this.isHoveringCollapsedSecondLevelPanel = false;
          }
          this.globalMouseLeaveTimeout = null;
        }, 200); // Reasonable delay for smooth hover experience
      }
    });

    // Track mouse leave events on the document for immediate cleanup
    document.addEventListener('mouseleave', () => {
      // Clear any existing timeout
      if (this.globalMouseLeaveTimeout) {
        clearTimeout(this.globalMouseLeaveTimeout);
        this.globalMouseLeaveTimeout = null;
      }

      // Hide panels for both collapsed and expanded sidebar
      this.hideAllPanels();
      // Reset all hover states for consistency
      this.isHoveringSidebar = false;
      this.isHoveringPanel = false;
      this.isHoveringFirstLevelPanel = false;
      this.isHoveringSecondLevelPanel = false;
      this.isHoveringCollapsedPanel = false;
      this.isHoveringCollapsedSecondLevelPanel = false;
    });

    // Also track mouse leave events on the sidebar container for immediate cleanup
    const sidebarContainer = document.querySelector('nav');
    if (sidebarContainer) {
      sidebarContainer.addEventListener('mouseleave', () => {
        // Clear any existing timeout
        if (this.globalMouseLeaveTimeout) {
          clearTimeout(this.globalMouseLeaveTimeout);
          this.globalMouseLeaveTimeout = null;
        }

        // Only hide panels for expanded sidebar - collapsed sidebar has its own logic
        if (this.sidebarService.isOpen()) {
          // Set a short timeout to hide panels when leaving sidebar
          this.globalMouseLeaveTimeout = setTimeout(() => {
            if (!this.isMouseOverSidebar && !this.isMouseOverAnyPanel) {
              this.hideAllPanels();
            }
            this.globalMouseLeaveTimeout = null;
          }, 100); // Short delay for smooth hover experience
        }
      });
    }

    // Track clicks outside sidebar to hide panels immediately
    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isClickOnSidebar = target.closest('nav');
      const isClickOnPanel = target.closest(
        '.panel-container, .floating-menu, .p-tieredmenu',
      );

      // If click is not on sidebar or panel, hide all panels immediately
      // But only for expanded sidebar - collapsed sidebar has its own logic
      if (
        !isClickOnSidebar &&
        !isClickOnPanel &&
        this.sidebarService.isOpen()
      ) {
        this.hideAllPanels();
      }
    });

    // Add more aggressive panel leave detection using MutationObserver
    // This ensures we catch all panel elements as they are created
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const panelElements = element.querySelectorAll(
              '.panel-container, .floating-menu, .p-tieredmenu',
            );

            panelElements.forEach((panel) => {
              panel.addEventListener('mouseleave', () => {
                // Clear any existing timeout
                if (this.globalMouseLeaveTimeout) {
                  clearTimeout(this.globalMouseLeaveTimeout);
                  this.globalMouseLeaveTimeout = null;
                }

                // Set a short timeout to hide panels when leaving any panel
                // But only for expanded sidebar - collapsed sidebar has its own logic
                if (this.sidebarService.isOpen()) {
                  this.globalMouseLeaveTimeout = setTimeout(() => {
                    if (!this.isMouseOverSidebar && !this.isMouseOverAnyPanel) {
                      this.hideAllPanels();
                    }
                    this.globalMouseLeaveTimeout = null;
                  }, 150); // Short delay for smooth hover experience
                }
              });
            });
          }
        });
      });
    });

    // Start observing the document for new panel elements
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy(): void {
    this.clearAllTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSidebarResizeStart(event: MouseEvent): void {
    if (!this.sidebarService.isOpen() || this.sidebarService.isResponsive()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = this.sidebarService.sidebarWidth();
    this.sidebarService.isResizing.set(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      this.sidebarService.setSidebarWidth(startWidth + delta);
    };

    const onMouseUp = () => {
      this.sidebarService.isResizing.set(false);
      globalThis.window?.removeEventListener('mousemove', onMouseMove);
      globalThis.window?.removeEventListener('mouseup', onMouseUp);
    };

    globalThis.window?.addEventListener('mousemove', onMouseMove);
    globalThis.window?.addEventListener('mouseup', onMouseUp);
  }

  onSidebarResizeReset(): void {
    this.sidebarService.resetSidebarWidth();
  }

  onSidebarResizeKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.sidebarService.setSidebarWidth(this.sidebarService.sidebarWidth() + 16);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.sidebarService.setSidebarWidth(this.sidebarService.sidebarWidth() - 16);
    } else if (event.key === 'Home' || event.key === '0') {
      event.preventDefault();
      this.sidebarService.resetSidebarWidth();
    }
  }

  private clearAllTimeouts(): void {
    const timeouts = [
      this.firstLevelTimeout,
      this.secondLevelTimeout,
      this.menuItemTimeout,
      this.collapsedPanelTimeout,
      this.globalMouseLeaveTimeout,
      this.panelHideFallbackTimeout,
    ];

    timeouts.forEach((timeout) => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });

    // Reset all to null for safety
    this.firstLevelTimeout = null;
    this.secondLevelTimeout = null;
    this.menuItemTimeout = null;
    this.collapsedPanelTimeout = null;
    this.globalMouseLeaveTimeout = null;
    this.panelHideFallbackTimeout = null;
  }
}
